import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

/** Settings → Customer events → Klaviyo. The private key is never sent back: only the last 4. */
export type KlaviyoStatus = {
  connected: boolean
  readable?: boolean
  enabled: boolean
  onsite?: boolean
  send_emails?: boolean
  key_hint?: string
  public_key?: string
  account_name?: string | null
  list?: { id: string; name: string | null } | null
  connected_at?: string
  last_sync_at?: string | null
  last_test?: { at: string; ok: boolean | null } | null
  counts?: { pending?: number; failed?: number; sent_24h?: number }
  failures?: { label: string | null; last_error: string | null; updated_at: string }[]
  can_manage: boolean
}
type StatusResponse = { klaviyo: KlaviyoStatus; list_error?: string | null }

const KEY = ["settings", "klaviyo"]

export function useKlaviyo() {
  return useQuery({ queryKey: KEY, queryFn: () => sdk.client.fetch<StatusResponse>("/admin/terify/settings/klaviyo") })
}

export function useKlaviyoLists(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, "lists"],
    enabled,
    staleTime: 60_000,
    queryFn: () => sdk.client.fetch<{ lists: { id: string; name: string }[] }>("/admin/terify/settings/klaviyo/lists"),
  })
}

function useKlaviyoMutation<V>(fn: (v: V) => Promise<StatusResponse | unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (r: any) => {
      if (r?.klaviyo) qc.setQueryData(KEY, r)
      else qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export const useConnectKlaviyo = () => useKlaviyoMutation((body: { api_key: string }) => sdk.client.fetch<StatusResponse>("/admin/terify/settings/klaviyo", { method: "POST", body }))
export const useDisconnectKlaviyo = () => useKlaviyoMutation(() => sdk.client.fetch<StatusResponse>("/admin/terify/settings/klaviyo", { method: "DELETE" }))
export const useKlaviyoOptions = () =>
  useKlaviyoMutation((body: { enabled?: boolean; onsite?: boolean; list_id?: string | null; send_emails?: boolean }) => sdk.client.fetch<StatusResponse>("/admin/terify/settings/klaviyo/options", { method: "POST", body }))
export const useTestKlaviyo = () => useKlaviyoMutation(() => sdk.client.fetch<{ test: { ok: boolean; message: string } }>("/admin/terify/settings/klaviyo/connection-test", { method: "POST", body: {} }))
export const useSyncKlaviyo = () =>
  useKlaviyoMutation((body: { retry_failed?: boolean }) => sdk.client.fetch<{ sync: Record<string, number | string>; klaviyo: KlaviyoStatus }>("/admin/terify/settings/klaviyo/sync", { method: "POST", body }))

/** Settings → Klaviyo → store emails: the template and the metric the flow needs. */
export type StoreEmailSetup = { template_id: string | null; metric: boolean; metric_name: string; template_name: string }
export function useStoreEmailSetup(enabled: boolean) {
  return useQuery({ queryKey: [...KEY, "email-template"], enabled, queryFn: () => sdk.client.fetch<StoreEmailSetup>("/admin/terify/settings/klaviyo/email-template") })
}
export function useCreateStoreEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => sdk.client.fetch<StoreEmailSetup>("/admin/terify/settings/klaviyo/email-template", { method: "POST", body: {} }),
    onSuccess: (r) => qc.setQueryData([...KEY, "email-template"], r),
  })
}
